import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
} from "@mui/material";
import {
  colorActionDanger,
  colorSurfaceError,
  colorBorderError,
  colorSurfaceDangerHover,
  colorBorder,
  colorTextPrimary,
  colorTextSecondary,
  colorTextDisabled,
  colorSurfaceHover,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface BlacklistOfferDialogProps {
  open: boolean;
  noShowCount: number;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

// Surfaced immediately after staff flag a no-show on a booker the backend
// reports as over the repeat-offender threshold. Confirming blocks future
// bookings; dismissing makes no request.
export const BlacklistOfferDialog = ({
  open,
  noShowCount,
  loading = false,
  onConfirm,
  onClose,
}: BlacklistOfferDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          border: `1px solid ${colorBorder}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.3 }}>
          {t("bookings.blacklist.title")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: "14px", color: colorTextSecondary, mb: 2.5 }}>
          {t("bookings.blacklist.body", { count: noShowCount })}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              borderColor: colorBorder,
              color: colorTextPrimary,
              borderRadius: "8px",
              "&:hover": {
                borderColor: colorTextDisabled,
                backgroundColor: colorSurfaceHover,
              },
            }}
          >
            {t("bookings.blacklist.dismiss")}
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "none",
              color: colorActionDanger,
              backgroundColor: colorSurfaceError,
              border: `1px solid ${colorBorderError}`,
              borderRadius: "8px",
              "&:hover": {
                boxShadow: "none",
                backgroundColor: colorSurfaceDangerHover,
              },
            }}
          >
            {t("bookings.blacklist.confirm")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
